/**
 * Canonical days/hours display utilities for vacation grids.
 *
 * Mirrors the backend `decomposeSignedHours` contract in `@evta/api-vacation` so
 * the frontend can trust the backend's `details.totalHours` as the source of truth
 * and decompose once at render time. The three callers' ad-hoc re-aggregation
 * logic (hardcoded * 8, Math.floor on signed divisions, recompose-then-redecompose)
 * is replaced by `toSignedHours` + `formatDaysHours`.
 *
 * @see docs/services/vacation/fractional-hours-display.md
 */

export type DecomposeOpts = { snap?: "quarter" | "exact" };

/**
 * Sign-safe decomposition of a total-hours value into `{days, hours}`.
 *
 * - `"exact"` (default): pure Math.trunc + native `%` — no rounding.
 * - `"quarter"`: pre-rounds to nearest 0.25 h then epsilon-snaps to whole days
 *   when within 0.01 h of `k * orgHours`.
 */
export function decomposeSignedHoursFE(
  totalHours: number,
  orgHours: number,
  opts: DecomposeOpts = {},
): { days: number; hours: number } {
  const snap = opts.snap ?? "exact";
  let value = totalHours;
  if (snap === "quarter") {
    value = Math.round(value * 4) / 4;
    const nearestWholeDay = Math.round(value / orgHours);
    if (Math.abs(value - nearestWholeDay * orgHours) < 0.01) {
      return { days: nearestWholeDay + 0, hours: 0 };
    }
  }
  const days = Math.trunc(value / orgHours) + 0;
  const hours = parseFloat((value % orgHours).toFixed(2)) + 0;
  return { days, hours };
}

/**
 * Coerce a `details.totalHours` field (legacy records may be string, number, or
 * missing) to a signed numeric value. Mirrors the backend defensive pattern in
 * `@evta/api-vacation/src/utils/index.ts` (`getEmployeeVacationAvailableInHours`).
 *
 * @param value     `details.totalHours` — usually `number`, sometimes `string`
 *                   (pre-BG-02 records), rarely missing (legacy migration gaps).
 * @param days      `details.days` fallback input.
 * @param hours     `details.hours` fallback input.
 * @param orgHours  org hours-in-workday; used only for the fallback composition.
 * @returns signed total hours.
 */
export function toSignedHours(
  value: unknown,
  days?: number,
  hours?: number,
  orgHours?: number,
): number {
  const coerced = Number(value);
  if (Number.isFinite(coerced)) return coerced;
  const d = typeof days === "number" ? days : 0;
  const h = typeof hours === "number" ? hours : 0;
  const o = typeof orgHours === "number" ? orgHours : 0;
  return d * o + h;
}

/**
 * Format a signed total-hours value as a display string.
 *
 * Contract:
 * - Sign lifted once to the outside — output contains at most one leading `-`.
 * - Quarter-hour snap by default (`opts.snap = "quarter"`); pass `"exact"` to skip.
 * - Always emits `0 Days X Hours` when decomposed days = 0 and hours != 0 (locked
 *   decision 2026-04-21 for consistent column alignment).
 * - Empty cell (`"-"`) when the value decomposes to zero days AND zero hours.
 * - Plural `Days` on the zero-days branch regardless of `X` (also a locked
 *   decision — grammar loses to column alignment).
 * - Plural pivot for `Hours`: exactly 1 → `Hour`, anything else → `Hours`.
 */
export function formatDaysHours(
  totalHours: number,
  orgHours: number,
  opts: DecomposeOpts = {},
): string {
  const sign = totalHours < 0 ? "-" : "";
  const abs = Math.abs(totalHours);
  const { days, hours } = decomposeSignedHoursFE(abs, orgHours, { snap: opts.snap ?? "quarter" });

  if (days === 0 && hours === 0) return "-";

  const hoursLabel = (h: number) => `${Number(h)} ${h === 1 ? "Hour" : "Hours"}`;
  const daysWord = (d: number) => (d === 1 ? "Day" : "Days");

  if (days === 0) return `${sign}0 Days ${hoursLabel(hours)}`;
  if (hours === 0) return `${sign}${days} ${daysWord(days)}`;
  return `${sign}${days} ${daysWord(days)} ${hoursLabel(hours)}`;
}

/**
 * Format a signed integer-minute duration as `"Xd Xh Xmin"`, omitting zero-valued
 * segments. Pure real-clock helper (no orgHours), used for validator error echo
 * like `"Your duration is 4h 10min"`.
 *
 * - `0` returns `"0min"` (stable non-empty string for callers; the duration
 *   validator never displays this because the `to <= from` branch fires first).
 * - Sign-lifted: `-125 → "-2h 5min"`.
 */
export function formatDurationLiteral(totalMinutes: number): string {
  const total = Math.round(totalMinutes);
  if (total === 0) return "0min";
  const abs = Math.abs(total);
  const d = Math.trunc(abs / (60 * 24));
  const h = Math.trunc((abs % (60 * 24)) / 60);
  const m = abs % 60;
  const segments = [
    d > 0 ? `${d}d` : null,
    h > 0 ? `${h}h` : null,
    m > 0 ? `${m}min` : null,
  ].filter(Boolean);
  return `${total < 0 ? "-" : ""}${segments.join(" ")}`;
}

/**
 * Format a signed total-hours value as `"X Day(s) and X hours"` — the long shape
 * the backend emits for synthetic rows (Earned / Carried Over / Lost / Used On OB)
 * and the shape BG-03 introduced at three FE sites (time-off-history dialog,
 * reports Days column, My-Team Time Off Details).
 *
 * Single canonical formatter for the BG-04 unification — replaces three ad-hoc
 * `getDaysUsedLabel` helpers that used raw `Math.trunc` + `% orgHours` without
 * quarter snap (so Flex-Day partials surfaced as `"3.04 hours"` / `"3.52 hours"`).
 *
 * Contract:
 * - `snap` defaults to `"quarter"`. Pass `{ snap: "exact" }` to preserve raw.
 * - Sign lifted once to the outside: `-12 → "-1 Day(s) and 4 hours"` (never
 *   `"-1 Day(s) and -4 hours"`).
 * - Exact zero renders `"0 Day(s) and 0 hours"` (matches backend output —
 *   does NOT collapse to `"-"` like `formatDaysHours` does for grid cells).
 * - Whole-day values render as `"N Day(s) and 0 hours"` so Days/Hours columns
 *   stay aligned regardless of value (locked decision — grammar loses to column
 *   consistency, same rationale as `formatDaysHours`).
 */
export function formatDaysAndHoursLong(
  totalHours: number,
  orgHours: number,
  opts: DecomposeOpts = {},
): string {
  const sign = totalHours < 0 ? "-" : "";
  const abs = Math.abs(totalHours);
  const { days, hours } = decomposeSignedHoursFE(abs, orgHours, {
    snap: opts.snap ?? "quarter",
  });
  return `${sign}${days} Day(s) and ${hours} hours`;
}
